import { useState, useMemo, useCallback } from 'react'
import { DEFAULT_VISIBILITY } from '../constants/visibilityConfig'
import type { FieldVisibility } from '@/types'

/**
 * Map old field names to new DB field names
 */
const OLD_TO_NEW_FIELD_MAP = new Map([
    ['autor', 'author'],
    ['categoria', 'category'],
    ['category', 'category'],
    ['idioma', 'language'],
    ['editorial', 'publisher'],
    ['series', 'series'],
    ['series_number', 'series_number'],
    ['formato', 'format'],
    ['estado', 'status'],
    ['progreso', 'progress'],
    ['ubicacion', 'location'],
    ['notas', 'notes'],
    ['sinopsis', 'synopsis'],
    ['paginas', 'page_count'],
    ['fecha', 'created_at'],
])

/**
 * Return type for useFieldVisibility hook
 */
interface UseFieldVisibilityReturn {
    visibility: FieldVisibility
    toggleField: (fieldKey: keyof FieldVisibility) => void
    resetToDefaults: () => void
    isConfigurable: boolean
    allowedFields: string[] | null
}

/**
 * Custom hook for managing field visibility in book views
 * 
 * Unified hook for both Full Cards and Extended List views.
 * Handles localStorage persistence and respects shared view settings.
 */
export function useFieldVisibility(
    isSharedView: boolean = false,
    sharedSettings: string[] | null = null
): UseFieldVisibilityReturn {
    const STORAGE_KEY = isSharedView ? 'bookView_sharedVisibility' : 'bookView_fieldVisibility'

    // Load saved visibility from localStorage or use defaults
    const [localVisibility, setLocalVisibility] = useState<FieldVisibility>(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY)
            if (saved) {
                const parsed = JSON.parse(saved) as FieldVisibility
                // Merge with defaults to ensure any new fields are included
                return { ...DEFAULT_VISIBILITY, ...parsed }
            }
        } catch (error) {
            console.error('Error loading visibility from localStorage:', error)
        }

        return { ...DEFAULT_VISIBILITY }
    })

    // Get allowed fields (what the owner permits)
    const allowedFields = useMemo(() => {
        if (!isSharedView || !sharedSettings) return null

        const allowed = new Set(['title', 'author']) // Always allowed

        if (Array.isArray(sharedSettings)) {
            sharedSettings.forEach((oldFieldName) => {
                const newFieldName = OLD_TO_NEW_FIELD_MAP.get(oldFieldName) || oldFieldName
                allowed.add(newFieldName)
            })
        }

        return Array.from(allowed)
    }, [isSharedView, sharedSettings])

    // Effective visibility respects shared settings when viewing shared libraries
    const effectiveVisibility = useMemo(() => {
        if (isSharedView && allowedFields) {
            // In shared view: combine allowed fields with viewer preferences
            const result: Partial<FieldVisibility> = {}

            allowedFields.forEach((fieldName) => {
                // If viewer has preference, use it; otherwise default to true
                const viewerPreference = Object.hasOwn(localVisibility, fieldName)
                    ? localVisibility[fieldName as keyof FieldVisibility]
                    : true
                result[fieldName as keyof FieldVisibility] = viewerPreference
            })

            return result as FieldVisibility
        }
        return localVisibility
    }, [isSharedView, allowedFields, localVisibility])

    /**
     * Toggle visibility of a field
     * In shared views, only allowed fields can be toggled
     */
    const toggleField = useCallback(
        (fieldKey: keyof FieldVisibility) => {
            // In shared views, check if field is allowed
            if (isSharedView && allowedFields && !allowedFields.includes(fieldKey)) {
                console.warn(`Cannot toggle field "${fieldKey}" - not allowed by owner`)
                return
            }

            setLocalVisibility((prev) => {
                const updated = {
                    ...prev,
                    [fieldKey]: !prev[fieldKey],
                }

                // Persist to localStorage
                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
                } catch (error) {
                    console.error('Error saving visibility to localStorage:', error)
                }

                return updated
            })
        },
        [isSharedView, allowedFields, STORAGE_KEY]
    )

    /**
     * Reset to default visibility
     */
    const resetToDefaults = useCallback(() => {
        setLocalVisibility({ ...DEFAULT_VISIBILITY })

        try {
            localStorage.removeItem(STORAGE_KEY)
        } catch (error) {
            console.error('Error removing visibility from localStorage:', error)
        }
    }, [STORAGE_KEY])

    return {
        visibility: effectiveVisibility,
        toggleField,
        resetToDefaults,
        isConfigurable: true, // Always configurable, but limited to allowed fields in shared views
        allowedFields, // Return for UI to show which fields are readonly
    }
}
